/* =========================================================
   Vehicle Tracking Utilities, Design Tokens & Geometry
   (100% Real API Data Driven — No Static / Mock Data)
   ========================================================= */

export const R_EARTH = 6371000;
export const toRad = (d) => (d * Math.PI) / 180;

export function hav(a, b) {
  if (!a || !b) return 0;
  const dLat = toRad(b[1] - a[1]);
  const dLng = toRad(b[0] - a[0]);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a[1])) * Math.cos(toRad(b[1])) * Math.sin(dLng / 2) ** 2;
  return 2 * R_EARTH * Math.asin(Math.sqrt(s));
}

export function bearing(a, b) {
  if (!a || !b) return 0;
  let pA = a;
  let pB = b;
  // Automatically normalize if [lat, lng] was passed instead of [lng, lat]
  if (Array.isArray(pA) && pA.length >= 2 && pA[0] < 45 && pA[1] > 55) {
    pA = [pA[1], pA[0]];
  }
  if (Array.isArray(pB) && pB.length >= 2 && pB[0] < 45 && pB[1] > 55) {
    pB = [pB[1], pB[0]];
  }
  if (pA[0] === pB[0] && pA[1] === pB[1]) return 0;
  const y = Math.sin(toRad(pB[0] - pA[0])) * Math.cos(toRad(pB[1]));
  const x =
    Math.cos(toRad(pA[1])) * Math.sin(toRad(pB[1])) -
    Math.sin(toRad(pA[1])) * Math.cos(toRad(pB[1])) * Math.cos(toRad(pB[0] - pA[0]));
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

export function pointInPoly(p, poly) {
  if (!p || !poly || !poly.length) return false;
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i];
    const [xj, yj] = poly[j];
    if ((yi > p[1]) !== (yj > p[1]) && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

export function circlePoly(center, radiusM, n = 64) {
  const out = [];
  for (let i = 0; i <= n; i++) {
    const a = (i / n) * 2 * Math.PI;
    const dx = radiusM * Math.cos(a);
    const dy = radiusM * Math.sin(a);
    out.push([
      center[0] + ((dx / (R_EARTH * Math.cos(toRad(center[1])))) * 180) / Math.PI,
      center[1] + ((dy / R_EARTH) * 180) / Math.PI,
    ]);
  }
  return out;
}

export function routeLen(r) {
  if (!r || r.length < 2) return 0;
  let s = 0;
  for (let i = 1; i < r.length; i++) s += hav(r[i - 1], r[i]);
  return s;
}

export function pointAt(r, t) {
  if (!r || r.length === 0) return { p: [67.01, 24.88], h: 0 };
  if (r.length === 1) return { p: r[0], h: 0 };
  const total = routeLen(r);
  if (total === 0) return { p: r[0], h: 0 };
  let target = Math.max(0, Math.min(1, t)) * total;
  for (let i = 1; i < r.length; i++) {
    const seg = hav(r[i - 1], r[i]);
    if (target <= seg || i === r.length - 1) {
      const f = seg ? Math.min(1, target / seg) : 0;
      return {
        p: [
          r[i - 1][0] + (r[i][0] - r[i - 1][0]) * f,
          r[i - 1][1] + (r[i][1] - r[i - 1][1]) * f,
        ],
        h: bearing(r[i - 1], r[i]),
      };
    }
    target -= seg;
  }
  return { p: r[r.length - 1], h: 0 };
}

export function sliceRoute(r, t0, t1) {
  if (!r || r.length < 2) return r || [];
  const total = routeLen(r);
  if (total === 0) return r;
  const a = t0 * total;
  const b = t1 * total;
  const out = [pointAt(r, t0).p];
  let acc = 0;
  for (let i = 1; i < r.length; i++) {
    acc += hav(r[i - 1], r[i]);
    if (acc > a && acc < b) out.push(r[i]);
  }
  out.push(pointAt(r, t1).p);
  return out;
}

export function tAtVertex(r, idx) {
  const tot = routeLen(r);
  return tot > 0 ? routeLen(r.slice(0, idx + 1)) / tot : 0;
}

export function findFractionOnRoute(r, pt) {
  if (!r || r.length < 2 || !pt) return 0;
  const total = routeLen(r);
  if (total <= 0) return 0;

  let minSqDist = Infinity;
  let bestDist = 0;
  let accumDist = 0;

  for (let i = 1; i < r.length; i++) {
    const p1 = r[i - 1];
    const p2 = r[i];
    const segLen = hav(p1, p2);

    const dx = p2[0] - p1[0];
    const dy = p2[1] - p1[1];
    const lenSq = dx * dx + dy * dy;

    let projFraction = 0;
    if (lenSq > 0) {
      projFraction = Math.max(0, Math.min(1, ((pt[0] - p1[0]) * dx + (pt[1] - p1[1]) * dy) / lenSq));
    }

    const projLng = p1[0] + projFraction * dx;
    const projLat = p1[1] + projFraction * dy;
    const dLng = pt[0] - projLng;
    const dLat = pt[1] - projLat;
    const sqDist = dLng * dLng + dLat * dLat;

    if (sqDist < minSqDist) {
      minSqDist = sqDist;
      bestDist = accumDist + projFraction * segLen;
    }

    accumDist += segLen;
  }

  return Math.max(0, Math.min(1, bestDist / total));
}

export function boundsOf(points) {
  const b = [
    [Infinity, Infinity],
    [-Infinity, -Infinity],
  ];
  (points || []).forEach((p) => {
    if (!p || typeof p[0] !== 'number' || typeof p[1] !== 'number') return;
    b[0][0] = Math.min(b[0][0], p[0]);
    b[0][1] = Math.min(b[0][1], p[1]);
    b[1][0] = Math.max(b[1][0], p[0]);
    b[1][1] = Math.max(b[1][1], p[1]);
  });
  if (b[0][0] === Infinity || b[0][0] === b[1][0]) {
    const c = points && points[0] ? points[0] : [67.01, 24.88];
    return [
      [c[0] - 0.05, c[1] - 0.05],
      [c[0] + 0.05, c[1] + 0.05],
    ];
  }
  return b;
}

/* =========================================================
   MAP TILE CONFIGURATION (Esri World Street Standard)
   ========================================================= */

export const MAP_CONFIG = {
  active: 'esri',
  providers: {
    osm: {
      label: 'OpenStreetMap (Streets & Roads)',
      tiles: [
        'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
        'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
        'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png',
      ],
      tileSize: 256,
      maxzoom: 19,
      attribution: '© OpenStreetMap contributors',
    },
    satellite: {
      label: 'Satellite Imagery',
      tiles: [
        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      ],
      tileSize: 256,
      maxzoom: 17,
      attribution: '© Esri Satellite',
    },
    esri: {
      label: 'Esri World Street',
      tiles: [
        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}',
      ],
      tileSize: 256,
      maxzoom: 17,
      attribution: '© Esri',
    },
    carto: {
      label: 'Carto Voyager (Streets)',
      tiles: [
        'https://a.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}@2x.png',
        'https://b.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}@2x.png',
        'https://c.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}@2x.png',
      ],
      tileSize: 256,
      maxzoom: 19,
      attribution: '© CARTO, © OpenStreetMap',
    },
    schematic: {
      label: 'Offline schematic (no tiles)',
      tiles: null,
      attribution: 'Schematic basemap · FMS Vehicle Tracking',
    },
  },
};

/* =========================================================
   GEOCODING & LOCATION HELPERS
   ========================================================= */

export function placeName(p) {
  if (!p || !Array.isArray(p) || p.length < 2) return 'Awaiting coordinates';
  const lng = Number(p[0]);
  const lat = Number(p[1]);
  if (isNaN(lng) || isNaN(lat) || (lng === 0 && lat === 0)) return 'Awaiting coordinates';
  return `${lat.toFixed(4)}°N, ${lng.toFixed(4)}°E`;
}

export function addressHtml(p, prov = '', lastTs = null) {
  if (!p || !p.length) return '';
  const lat = Number(p[1]).toFixed(4);
  const lng = Number(p[0]).toFixed(4);
  return `<b>${lastTs ? 'Last known location' : 'Current location'}</b><div class="ad-l">${lat}°N, ${lng}°E</div><div class="ad-c"><span>Lat</span><b class="mono">${lat}</b><span>Long</span><b class="mono">${lng}</b></div>${lastTs ? `<small>Recorded ${dt(lastTs)}</small>` : ''}`;
}

export function addrTip(p, lastTs) {
  if (!p || !Array.isArray(p) || p.length < 2) return '';
  const lat = Number(p[1]).toFixed(6);
  const lng = Number(p[0]).toFixed(6);
  return `<b>${lastTs ? 'Last known location' : 'Current location'}</b><div class="ad-l">${lat}°N, ${lng}°E</div><div class="ad-c"><span>Lat</span><b class="mono">${lat}</b><span>Long</span><b class="mono">${lng}</b></div>${lastTs ? `<small>Recorded ${dt(lastTs)}</small>` : ''}`;
}

export const placeShort = (n) => {
  n = String(n || '');
  const m = n.match(/,\s*([^,]+)$/);
  return m
    ? m[1].trim()
    : n.replace(
        /\s+(Distribution Center|Retail Hub|Warehouse|Terminal|Depot|Distributor|Plant|DC|Mill|Hub)$/i,
        ''
      );
};

export const odShort = (a, b) => {
  let x = placeShort(a);
  let y = placeShort(b);
  if (x === y) {
    x = String(a).split(',')[0];
    y = String(b).split(',')[0];
  }
  return [x, y];
};

export const nearName = (p) => placeName(p);

/* =========================================================
   STATUS, BADGES & DESIGN TOKENS
   ========================================================= */

export const STATUS_HEX = {
  moving: '#248413',
  idle: '#B26800',
  stopped: '#D13B3B',
  inactive: '#7A8099',
  offline: '#7A8099',
  alert: '#D13B3B',
};

export const STATUS_PILL = {
  moving: 'pill moving',
  idle: 'pill idle',
  stopped: 'pill stopped',
  inactive: 'pill offline',
  offline: 'pill offline',
  alert: 'pill alert',
};

export const STATUS_LABEL = {
  moving: 'Moving',
  idle: 'Idle',
  stopped: 'Stopped',
  inactive: 'Offline',
  offline: 'Offline',
  alert: 'Alert',
};

export const TRIP_STATUSES = [
  'Awaiting for Trip',
  'Trip Started',
  'In-Transit',
  'Completed',
];

export const VEHICLE_TYPES = [
  'UD-GDE', 'UD-GWE', 'NPR', 'FVR', 'NKR', 'Daewoo-V3TEF',
  'PORTER H-100', 'D-Max', 'Shahzore', 'Prime Mover', 'Car', '40ft Flat Bed',
];

export const SPEED_LIMIT = { LTV: 100, HTV: 80 };

export const FUEL_CONFIG = {
  byType: {
    'UD-GDE': 2.8, 'UD-GWE': 2.5, 'Prime Mover': 2.7, '40ft Flat Bed': 2.8, 'FVR': 3.5,
    'NPR': 5.5, 'Daewoo-V3TEF': 3.0, 'NKR': 7, 'Shahzore': 8, 'PORTER H-100': 10,
    'D-Max': 9, 'Car': 12,
  },
  idleLph: { LTV: 0.8, HTV: 2.2 },
  idleEnabled: true,
  override: {},
};

export const TRIP_META = {};

export function tripStatusOf(v) {
  if (!v || !v.trip) return '—';
  if (typeof v.trip === 'object') {
    const rawSt = (v.trip.status || v.trip.stage || '').toLowerCase();
    if (rawSt.includes('transit')) return 'In-Transit';
    if (rawSt.includes('complete')) return 'Completed';
    if (rawSt.includes('start')) return 'Trip Started';
    return v.trip.status || 'In-Transit';
  }
  return typeof v.trip === 'string' ? v.trip : 'In-Transit';
}

export function kmpl(v) {
  return (
    FUEL_CONFIG.override[v?.reg] ||
    FUEL_CONFIG.byType[v?.type] ||
    (v?.cat === 'HTV' ? 3 : 9)
  );
}

export function fuelEst(v, km, idleMin) {
  const drive = km / kmpl(v);
  const idle = FUEL_CONFIG.idleEnabled
    ? (idleMin / 60) * FUEL_CONFIG.idleLph[v?.cat || 'LTV']
    : 0;
  return { drive, idle, total: drive + idle };
}

export function fuelToday(v) {
  return fuelEst(v, v?.km || 0, v?.idle || 0);
}

/* =========================================================
   DATE, TIME & DURATION FORMATTERS
   ========================================================= */

export const pad = (n) => String(n).padStart(2, '0');

export function clock(ts) {
  if (!ts) return '--:--';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return String(ts);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function dt(ts) {
  if (!ts) return '--';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return String(ts);
  const mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getMonth()];
  return `${pad(d.getDate())} ${mon} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function dmy(ts) {
  if (!ts) return '--';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return String(ts);
  const mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getMonth()];
  return `${pad(d.getDate())}/${mon}`;
}

export function dur(min) {
  min = Math.round(min || 0);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}h ${String(m).padStart(2, '0')}m`;
}

export function dshort(m) {
  m = Math.round(m || 0);
  return m < 60 ? `${m}m` : `${Math.floor(m / 60)}h ${m % 60}m`;
}

export function ago(ts) {
  if (!ts) return '—';
  const s = Math.max(0, (Date.now() - ts) / 1000);
  if (s < 60) return `${Math.round(s)}s ago`;
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) {
    const h = Math.floor(s / 3600);
    const m = Math.round((s % 3600) / 60);
    return `${h}h ${m}m ago`;
  }
  return `${Math.round(s / 86400)}d ago`;
}

export const agoShort = (ms) => {
  const m = Math.max(1, Math.round(ms / 60000));
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d} day${d === 1 ? '' : 's'}`;
};

export const today0 = () => new Date(new Date().setHours(0, 0, 0, 0)).getTime();

/* =========================================================
   REAL VEHICLE STATE RESOLVERS
   ========================================================= */

export function todaySplitOf(v, i) {
  const end = i?.offline ? v?.last : Date.now();
  const drive = v?.drive != null ? v.drive : ((v?.km || 0) / Math.max(30, v?.spd || 45)) * 60;
  const idle = v?.idle != null ? v.idle : 0;
  const stop =
    (i?.status === 'stopped' && !i?.offline ? (Date.now() - (v?.since || end)) / 60000 : 0) +
    ((v?.km || 0) > 100 ? 25 : 10);
  const span = drive + idle + stop;
  return { drive, idle, stop, span, start: end - span * 60000 };
}

export function normalizeStatus(rawStatus, speed, ignition) {
  const s = String(rawStatus || '').toUpperCase().trim();
  const spd = Number(speed || 0);
  if (spd > 0) return 'moving';
  if (s === 'IDLE' || s === 'RESTING' || s === 'LOADING' || (ignition && spd === 0)) return 'idle';
  if (s === 'INACTIVE' || s === 'OFFLINE') return 'inactive';
  return 'stopped';
}

export function info(v) {
  if (!v) return {};
  const hasCoords =
    v.lat != null &&
    v.lng != null &&
    !isNaN(Number(v.lat)) &&
    !isNaN(Number(v.lng)) &&
    Number(v.lat) !== 0 &&
    Number(v.lng) !== 0;

  // Real [lng, lat] coordinate pair for MapLibre (null if not yet acquired)
  const p = hasCoords ? [Number(v.lng), Number(v.lat)] : null;
  let h = v.h != null && !isNaN(Number(v.h)) ? Number(v.h) : 0;

  // If heading is 0, infer direction of travel from polyline, route or trip endpoints
  if (h === 0) {
    const poly = v.polylineGeoJson || v.route;
    if (poly && poly.length >= 2) {
      if (!p) {
        h = bearing(poly[0], poly[1]);
      } else {
        let minDist = Infinity;
        let bestIdx = 0;
        for (let idx = 0; idx < poly.length - 1; idx++) {
          const midX = (poly[idx][0] + poly[idx + 1][0]) / 2;
          const midY = (poly[idx][1] + poly[idx + 1][1]) / 2;
          const d = (p[0] - midX) ** 2 + (p[1] - midY) ** 2;
          if (d < minDist) {
            minDist = d;
            bestIdx = idx;
          }
        }
        h = bearing(poly[bestIdx], poly[bestIdx + 1]);
      }
    } else {
      const stObj = v.start_trip || v.trip?.start_trip || (Array.isArray(v.trip?.stops) && v.trip.stops[0]) || null;
      const endObj = v.end_trip || v.trip?.end_trip || (Array.isArray(v.trip?.stops) && v.trip.stops[v.trip.stops.length - 1]) || null;
      if (stObj && endObj) {
        const sLng = Number(stObj.longitude || stObj.lng);
        const sLat = Number(stObj.latitude || stObj.lat);
        const eLng = Number(endObj.longitude || endObj.lng);
        const eLat = Number(endObj.latitude || endObj.lat);
        if (!isNaN(sLng) && !isNaN(sLat) && !isNaN(eLng) && !isNaN(eLat) && sLng !== 0 && eLng !== 0) {
          h = bearing([sLng, sLat], [eLng, eLat]);
        }
      }
    }
  }

  const normSt = normalizeStatus(v.st, v.spd, v.ignition);
  const inactive = normSt === 'inactive';
  const offline = normSt === 'offline' || inactive;
  const status = normSt;
  const display = status;
  const ign = v.ignition !== undefined
    ? (v.ignition ? 'On' : 'Off')
    : ((inactive || offline) ? (v.spd > 0 ? 'On' : 'Off') : status === 'stopped' ? 'Off' : 'On');
  const tripStatus = v.tripStatus || tripStatusOf(v);
  const onTrip = tripStatus === 'Trip Started' || tripStatus === 'In-Transit' || Boolean(v.trip);

  return {
    p,
    h,
    offline,
    inactive,
    status,
    display,
    ign,
    tripStatus,
    onTrip,
    prov: { id: 'P1', name: 'GPS Tracker' },
    loc: v.loc && v.loc !== 'Awaiting coordinates'
      ? v.loc
      : (p ? placeName(p) : 'Awaiting coordinates'),
  };
}

export function tripOf(v) {
  if (!v || !v.trip || tripStatusOf(v) === '—') return null;

  if (typeof v.trip === 'object') {
    const t = v.trip;
    const st = tripStatusOf(v);
    const meta = t.trip_meta_info || {};
    const total = Number(meta.total_distance || 0);
    const driven = Number(meta.covered_distance || 0);
    const frac = meta.covered_percentage != null
      ? Math.max(0, Math.min(1, Number(meta.covered_percentage) / 100))
      : total > 0 ? Math.max(0, Math.min(1, driven / total)) : (st === 'Completed' ? 1 : 0);
    const from = t.start_trip?.location || 'Origin';
    const to = t.end_trip?.location || 'Destination';
    const a = (t.start_trip?.longitude != null && t.start_trip?.latitude != null)
      ? [Number(t.start_trip.longitude), Number(t.start_trip.latitude)]
      : null;
    const b = (t.end_trip?.longitude != null && t.end_trip?.latitude != null)
      ? [Number(t.end_trip.longitude), Number(t.end_trip.latitude)]
      : null;
    const route = (a && b) ? [a, b] : [];
    const startedTs = t.actual_start_at ? new Date(t.actual_start_at).getTime() : null;
    const arrivedTs = t.expected_end_at ? new Date(t.expected_end_at).getTime() : null;

    return {
      id: t.trip_id || `TRIP-${t.id}`,
      st,
      m: { origin: from, dest: to },
      route,
      a: a || route[0],
      b: b || route[route.length - 1],
      from,
      to,
      total,
      driven,
      left: Math.max(0, total - driven),
      frac,
      plan: startedTs || Date.now(),
      startedTs,
      lateMin: 0,
      arrivedTs,
      cust: t.customer?.name || v.cust || '',
    };
  }
  return null;
}

export const lastTripOf = (v) => v?.lastTrip || null;

export const offTrip = (v, i) => tripStatusOf(v) === '—' && i.status === 'moving' && !i.offline;

/* =========================================================
   SVG VEHICLE ICONS & ASSETS
   ========================================================= */

export const VT_KIND = (t) =>
  /tanker/i.test(t)
    ? 'tanker'
    : /container/i.test(t)
    ? 'container'
    : /bulker/i.test(t)
    ? 'bulker'
    : /trailer|prime mover|flat ?bed|^ud-g/i.test(t)
    ? 'trailer'
    : /van|hiace/i.test(t)
    ? 'van'
    : /pickup|porter|d-?max/i.test(t)
    ? 'pickup'
    : /^car$|sedan/i.test(t)
    ? 'car'
    : 'rigid';

export function topDownSVG(cat, col) {
  const glass = '#1B2340';
  if (cat === 'HTV') {
    return `<svg class="tdv-svg" width="17" height="40" viewBox="-1.5 -1.5 21 45" aria-hidden="true"><rect x="2" y="13" width="14" height="28" rx="2" fill="${col}" stroke="#fff" stroke-width="1.5"/><rect x="4" y="15.5" width="10" height="23" rx="1" fill="#ffffff" opacity=".4"/><rect x="7.6" y="10.5" width="2.8" height="3" fill="#2B2F45"/><rect x="2.5" y="1" width="13" height="10.5" rx="3" fill="${col}" stroke="#fff" stroke-width="1.5"/><path d="M4.3 3.4h9.4l-1 3.2H5.3z" fill="${glass}"/><rect x="5" y="7.8" width="8" height="2.4" rx="1" fill="rgba(255,255,255,.35)"/></svg>`;
  }
  return `<svg class="tdv-svg" width="15" height="28" viewBox="-1.5 -1.5 19 33" aria-hidden="true"><rect x="1.5" y="1" width="13" height="28" rx="5.5" fill="${col}" stroke="#fff" stroke-width="1.5"/><path d="M3.6 8.2Q8 5.6 12.4 8.2L11.6 11.6H4.4z" fill="${glass}"/><rect x="4.4" y="12.6" width="7.2" height="7.4" rx="1.6" fill="#ffffff" opacity=".3"/><path d="M4.4 21.4H11.6L12.2 24.4Q8 26.4 3.8 24.4z" fill="${glass}" opacity=".85"/><rect x="2.2" y="3" width="2" height="1.4" rx=".6" fill="#FFF6C8"/><rect x="11.8" y="3" width="2" height="1.4" rx=".6" fill="#FFF6C8"/></svg>`;
}

export function typeIconSVG(type, st) {
  const col = STATUS_HEX[st] || '#5C5F80';
  const W = (xs) =>
    xs.map((x) => `<circle cx="${x}" cy="11.6" r="1.9" fill="#2B2F45" stroke="#fff" stroke-width=".9"/>`).join('');
  const cab = '<path d="M19.5 4.5h3.8l3.2 3.2v3.3h-7z"/><path d="M20.6 5.6h2.3l1.9 2h-4.2z" fill="#fff" opacity=".75"/>';
  const k = VT_KIND(type || '');
  const body = {
    trailer: `<rect x="1" y="2" width="17.5" height="8.6" rx="1"/>${cab}${W([4, 7.6, 15.2, 23])}`,
    tanker: `<rect x="1" y="3" width="17.5" height="7.6" rx="3.8"/><path d="M6 3v7.6M12 3v7.6" stroke="#fff" stroke-width=".7" opacity=".6"/>${cab}${W([4, 7.6, 15.2, 23])}`,
    container: `<rect x="1" y="2" width="17.5" height="8.6" rx=".6"/><path d="M4 3v6.6M7 3v6.6M10 3v6.6M13 3v6.6M16 3v6.6" stroke="#fff" stroke-width=".6" opacity=".55"/>${cab}${W([4, 7.6, 15.2, 23])}`,
    bulker: `<path d="M1.5 4 Q10 0.8 18.5 4 L17 10.6 H3 Z"/>${cab}${W([4.5, 8, 15, 23])}`,
    rigid: `<rect x="2" y="2" width="15.5" height="8.6" rx="1"/>${cab}${W([5.5, 14, 23])}`,
    van: `<path d="M2 4.2Q2 2.4 4 2.4H19.5L25 6.6V10.6H2Z"/><path d="M19.2 3.6l3.8 3.2h-3.8z" fill="#fff" opacity=".75"/>${W([6.5, 20.5])}`,
    pickup: `<path d="M1.5 6.4H13V3.2h5.4l3.4 3.2H25.5V10.6H1.5Z"/><path d="M14 4.2h4l2.4 2.2H14z" fill="#fff" opacity=".75"/>${W([6, 21])}`,
    car: `<path d="M1.5 10.6V7.6l4-1.2 3.4-3.4h8.2l4 3.4 4 1.2v3z"/><path d="M9.6 4h3.4v2.6H7.2zM14 4h2.8l2.8 2.6H14z" fill="#fff" opacity=".75"/>${W([7, 20])}`,
  }[k] || `<rect x="2" y="2" width="15.5" height="8.6" rx="1"/>${cab}${W([5.5, 14, 23])}`;

  return `<svg viewBox="0 0 28 14" width="30" height="15" fill="${col}">${body}</svg>`;
}

/* =========================================================
   SYSTEM ICONS DICTIONARY
   ========================================================= */

export const IC = {
  dash: '<rect x="3" y="3" width="7" height="9" rx="1"/><rect x="14" y="3" width="7" height="5" rx="1"/><rect x="14" y="12" width="7" height="9" rx="1"/><rect x="3" y="16" width="7" height="5" rx="1"/>',
  map: '<path d="M9 4 3 6v14l6-2 6 2 6-2V4l-6 2-6-2z"/><path d="M9 4v14M15 6v14"/>',
  list: '<path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"/>',
  car: '<path d="M4.5 16.5V12l1.8-4.6A1.5 1.5 0 0 1 7.7 6.5h8.6a1.5 1.5 0 0 1 1.4.9L19.5 12v4.5"/><path d="M4.5 12h15"/><path d="M3.5 16.5h17"/><circle cx="7.5" cy="17" r="1.7"/><circle cx="16.5" cy="17" r="1.7"/>',
  truck: '<path d="M3 16V7a1 1 0 0 1 1-1h10v10"/><path d="M14 9h4l3 3v4h-7"/><circle cx="7.5" cy="17.5" r="1.8"/><circle cx="17.5" cy="17.5" r="1.8"/>',
  trip: '<circle cx="6" cy="19" r="2"/><circle cx="18" cy="5" r="2"/><path d="M8 19h8.5a3.5 3.5 0 0 0 0-7h-9a3.5 3.5 0 0 1 0-7H16"/>',
  history: '<path d="M3 12a9 9 0 1 0 3-6.7L3 8"/><path d="M3 3v5h5"/><path d="M12 7v5l3 2"/>',
  fence: '<path d="M12 21s-7-5.5-7-11a7 7 0 0 1 14 0c0 5.5-7 11-7 11z"/><circle cx="12" cy="10" r="2.5"/>',
  radar: '<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><path d="M12 12 19 5"/>',
  bell: '<path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/>',
  plug: '<path d="M9 2v6M15 2v6M6 8h12v4a6 6 0 0 1-12 0V8zM12 18v4"/>',
  pulse: '<path d="M22 12h-4l-3 9L9 3l-3 9H2"/>',
  users: '<circle cx="9" cy="8" r="4"/><path d="M2 21a7 7 0 0 1 14 0"/><path d="M17 11a3 3 0 1 0 0-6M22 21a6 6 0 0 0-4-5.6"/>',
  building: '<rect x="4" y="3" width="16" height="18" rx="1"/><path d="M9 7h1M14 7h1M9 11h1M14 11h1M9 15h1M14 15h1"/>',
  wrench: '<path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.5 2.5-2.5-2.5 2.5-2.5z"/>',
  report: '<path d="M4 20V10M10 20V4M16 20v-7M22 20H2"/>',
  gear: '<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 0 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 0 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 0 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 0 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z"/>',
  eye: '<path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/>',
  phone: '<path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2z"/>',
  pin: '<path d="M12 21s-7-5.5-7-11a7 7 0 0 1 14 0c0 5.5-7 11-7 11z"/><circle cx="12" cy="10" r="2.5"/>',
  play: '<path d="M7 4v16l13-8z"/>',
  pause: '<path d="M7 4h4v16H7zM13 4h4v16h-4z"/>',
  plus: '<path d="M12 5v14M5 12h14"/>',
  edit: '<path d="M4 20h4L20 8l-4-4L4 16v4z"/>',
  wifiOff: '<path d="M2 2l20 20M8.5 16.5a5 5 0 0 1 7 0M5 12.9a10 10 0 0 1 5.2-2.7M19 12.9a10 10 0 0 0-2.3-1.6M12 20h.01"/>',
  pauseS: '<path d="M8 5v14M16 5v14"/>',
  stop: '<rect x="6" y="6" width="12" height="12" rx="1"/>',
  nav: '<path d="M12 2 19 21 12 17 5 21z"/>',
  flag: '<path d="M5 21V4"/><path d="M5 4h11l-2 4 2 4H5"/>',
  check: '<path d="M5 12.5 10 17 19 7"/>',
  fuel: '<path d="M4 21V5a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v16"/><path d="M3 21h13M15 9h2a2 2 0 0 1 2 2v6a1.5 1.5 0 0 0 3 0V8l-3-3"/><path d="M7 7h5"/>',
  road: '<path d="M5 21 9 3M19 21 15 3M12 5v2M12 11v2M12 17v2"/>',
  key: '<circle cx="7.5" cy="15.5" r="4.5"/><path d="m10.7 12.3 9.8-9.8M17 6l3 3M14.5 8.5l2 2"/>',
  warn: '<path d="M12 3 2 21h20L12 3z"/><path d="M12 10v5M12 18h.01"/>',
  lock: '<rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/>',
  refresh: '<path d="M21 12a9 9 0 1 1-2.6-6.4L21 8"/><path d="M21 3v5h-5"/>',
  x: '<path d="M6 6l12 12M18 6 6 18"/>',
  search: '<circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/>',
  filter: '<path d="M3 5h18l-7 8.5V19l-4 2v-7.5z"/>',
  info: '<circle cx="12" cy="12" r="9"/><path d="M12 11v6M12 7.5h.01"/>',
  clock: '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>',
  speedo: '<path d="M4 17a8.5 8.5 0 1 1 16 0"/><path d="M12 14.5l4-5"/><circle cx="12" cy="15" r="1.2"/><path d="M6.5 11.5l1 .6M12 6.5v1.2M17.5 11.5l-1 .6"/>',
  hourglass: '<path d="M7 3h10M7 21h10M8 3c0 5 8 5 8 9s-8 4-8 9M16 3c0 5-8 5-8 9s8 4 8 9"/>',
  lastseen: '<path d="M3.5 12a8.5 8.5 0 1 0 2.5-6"/><path d="M3 4v4h4"/><path d="M12 8v4l3 2"/>',
  gauge: '<path d="M12 14l4-4"/><path d="M3.3 17a10 10 0 1 1 17.4 0"/>',
  chev: '<path d="m6 9 6 6 6-6"/>',
  signal: '<path d="M2 20h.01M7 20v-4M12 20v-8M17 20V8M22 20V4"/>',
};

export const ic = (n, s = '') =>
  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" ${s}>${
    IC[n] || ''
  }</svg>`;

export function keyMetric(v, i) {
  const kmIc = (name, label) =>
    `<span class="km-ic" title="${label}">${ic(name, 'width="12" height="12" stroke-width="1.6"')}</span>`;
  if (i?.offline) return { label: 'Last seen', val: ago(v?.last), html: `${kmIc('lastseen', 'Last seen')}${ago(v?.last)}` };
  if (i?.status === 'moving') return { label: 'Speed', val: `${v?.spd || 0} km/h`, html: `${kmIc('speedo', 'Speed')}${v?.spd || 0} km/h` };
  const d = dur((Date.now() - (v?.since || Date.now())) / 60000);
  return i?.status === 'idle'
    ? { label: 'Idling for', val: d, html: `${kmIc('hourglass', 'Idling for')}${d}` }
    : { label: 'Stopped for', val: d, html: `${kmIc('clock', 'Stopped for')}${d}` };
}

export function etaOf(v, i) {
  return null;
}

export function rowInfo(v, i) {
  if (i?.offline) return null;
  const still = i?.status !== 'moving';
  if (!still) {
    return i?.tripStatus === '—'
      ? { c: 'off', icon: 'road', html: `${Math.round(v?.km || 0)} km off-trip`, t: 'Driven today without trip' }
      : null;
  }
  return { c: 'm', icon: 'road', html: `Today ${Math.round(v?.km || 0)} km`, t: 'Distance today (GPS)' };
}

export function etaLine(v, i) {
  const r = rowInfo(v, i);
  if (!r) return '';
  return `<span class="num eta ri-${r.c}" title="${r.t}">${ic(r.icon, 'width="12" height="12" stroke-width="1.6"')}${r.html}</span>`;
}

export function ignChip(v, i) {
  const on = i?.ign === 'On';
  const t = i?.offline
    ? `Ignition ${on ? 'on' : 'off'} (last known · ${ago(v?.last)})`
    : on
    ? i?.status === 'idle'
      ? 'Ignition on · engine idling'
      : 'Ignition on'
    : `Ignition off since ${clock(v?.since)}`;
  return `<span class="ign-b ${on ? 'on' : 'off'} ${i?.offline ? 'ign-ls' : ''}" title="${t}" aria-label="${t}">${ic('key', 'width="11" height="11" stroke-width="2"')}</span>`;
}

export const TRIP_HEX = '#4014B8';
export const TRIP_DONE_HEX = '#7E66D6';
export const OFF_HEX = '#C2255C';
export const TRIP_LEFT_HEX = '#B9B3D9';

export const RUN_HEX = {
  trip: TRIP_HEX,
  off: OFF_HEX,
  idle: '#B26800',
  stopped: '#D13B3B',
  moving: '#248413',
};

export const RUN_LABEL = {
  trip: 'On a trip',
  off: 'Moving without trip',
  idle: 'Idle',
  stopped: 'Stopped',
};

export const VIO = {
  overspeed: { label: 'Overspeed', icon: 'speedo' },
  brake: { label: 'Harsh braking', icon: 'gauge' },
  accel: { label: 'Harsh acceleration', icon: 'nav' },
  corner: { label: 'Sharp cornering', icon: 'road' },
  idle: { label: 'Long idling', icon: 'hourglass' },
};

export function vioValue(e) {
  if (e?.type === 'overspeed') return `${e.val} km/h (limit ${e.lim})`;
  if (e?.type === 'idle') return `${e.val} min (limit ${e.lim})`;
  return `${e?.val?.toFixed ? e.val.toFixed(2) : e.val} g ${
    e?.type === 'brake' ? 'deceleration' : e?.type === 'accel' ? 'acceleration' : 'lateral'
  }`;
}

export const vioToday = (v) => (v?.vio || []).filter((e) => e?.t >= today0());
export const vioIn = (v, a, b) => (v?.vio || []).filter((e) => e?.t >= a && e?.t <= b);

export function vioSummary(list) {
  const c = {};
  (list || []).forEach((e) => (c[e.type] = (c[e.type] || 0) + 1));
  return Object.entries(c)
    .map(([k, n]) => `${VIO[k]?.label || k} ×${n}`)
    .join(', ');
}

export function tdShade(hex, f) {
  const n = parseInt(hex.slice(1), 16);
  const c = (x) => Math.min(255, Math.round(x * f));
  return `rgb(${c(n >> 16)},${c((n >> 8) & 255)},${c(n & 255)})`;
}
